import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { SellerBalanceById } from "@/components/SellerBalance";
import { PageHeader, DataTable, StatusBadge, EmptyState, StatCard } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Search, RotateCcw, Wallet, TrendingUp, Receipt, RefreshCw } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";

const TABS = [
  { id: "requested", label: "Requested", group: "requested" },
  { id: "ready", label: "Ready to Ship", group: "ready" },
  { id: "pickup", label: "Pickup", group: "pickup" },
  { id: "transit", label: "In Transit", group: "transit" },
  { id: "delivered", label: "Returned", group: "delivered" },
  { id: "cancelled", label: "Cancelled", group: "cancelled" },
  { id: "all", label: "All", group: null },
];

const REASON_LABELS = {
  damaged: "Damaged / defective", wrong_item: "Wrong item", not_as_described: "Not as described",
  size_fit: "Size / fit issue", quality: "Quality issue", missing_parts: "Missing parts",
  no_longer_needed: "No longer needed", other: "Other",
};

const PAGE_SIZE = 50;
const ALL = "all";
const DEFAULT_WINDOW_DAYS = 30;
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const inr = (n) =>
  n == null ? "-" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const B2CReturns = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useLocalStorageState("admin-b2c-returns:tab", "all");
  const [sellerId, setSellerId] = useLocalStorageState("admin-b2c-returns:seller", ALL);
  const [type, setType] = useLocalStorageState("admin-b2c-returns:type", ALL);
  const [aggregator, setAggregator] = useLocalStorageState("admin-b2c-returns:aggregator", ALL);
  const [dateFrom, setDateFrom] = useLocalStorageState("admin-b2c-returns:date_from", daysAgoISO(DEFAULT_WINDOW_DAYS));
  const [dateTo, setDateTo] = useLocalStorageState("admin-b2c-returns:date_to", "");
  const [search, setSearch] = useLocalStorageState("admin-b2c-returns:search", "");
  const [searchInput, setSearchInput] = useState(search);
  const [page, setPage] = useState(1);

  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: PAGE_SIZE, counts: {}, summary: {} });
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ sellers: [], aggregators: [] });

  const abortRef = useRef(null);
  const didSyncDeepLink = useRef(false);

  useEffect(() => {
    if (didSyncDeepLink.current) return;
    didSyncDeepLink.current = true;
    const grp = searchParams.get("status_group") || searchParams.get("tab");
    if (grp && TABS.some((t) => t.id === grp)) setActiveTab(grp);
  }, [searchParams, setActiveTab]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/b2c/admin/returns/filter-options`)
      .then((r) => { if (!cancelled) setOptions({ sellers: r.data?.sellers || [], aggregators: r.data?.aggregators || [] }); })
      .catch(() => { if (!cancelled) setOptions({ sellers: [], aggregators: [] }); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput, setSearch]);

  const tabGroup = (TABS.find((t) => t.id === activeTab) || TABS[TABS.length - 1]).group;

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (tabGroup) params.status_group = tabGroup;
      if (sellerId && sellerId !== ALL) params.seller_id = sellerId;
      if (type && type !== ALL) params.type = type;
      if (aggregator && aggregator !== ALL) params.aggregator = aggregator;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search) params.search = search;
      const { data: res } = await axios.get(`${API}/b2c/admin/returns`, { params, signal: controller.signal });
      setData({
        items: res.items || [], total: res.total || 0, page: res.page || 1,
        page_size: res.page_size || PAGE_SIZE, counts: res.counts || {}, summary: res.summary || {},
      });
    } catch (e) {
      if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      toast.error(errDetail(e, "Failed to load returns"));
    } finally {
      setLoading(false);
    }
  }, [page, tabGroup, sellerId, type, aggregator, dateFrom, dateTo, search]);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);
  useEffect(() => { setPage(1); }, [activeTab, sellerId, type, aggregator, dateFrom, dateTo, search]);

  const resetFilters = () => {
    setSellerId(ALL); setType(ALL); setAggregator(ALL);
    setDateFrom(daysAgoISO(DEFAULT_WINDOW_DAYS)); setDateTo(""); setSearch(""); setSearchInput("");
  };
  const hasFilters =
    (sellerId && sellerId !== ALL) || (type && type !== ALL) || (aggregator && aggregator !== ALL) ||
    !!dateFrom || !!dateTo || !!search;

  const tabCount = (id) => {
    const counts = data.counts || {};
    return id === "all" ? counts.all ?? data.total : counts[id];
  };

  const summary = data.summary || {};
  const marginTotal = summary.margin_total;
  const marginAccent = marginTotal == null ? "neutral" : marginTotal >= 0 ? "success" : "danger";
  const marginPctLine = summary.margin_pct != null ? `${Number(summary.margin_pct).toLocaleString("en-IN")}% of revenue` : undefined;

  const columns = [
    {
      key: "return", header: "Return #", primary: true,
      cell: (r) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/admin/b2c/returns/${r.id}`); }}
          className="text-sm font-medium text-brand hover:underline">
          {r.return_number}
        </button>
      ),
    },
    {
      key: "date", header: "Date", hideOnMobile: true,
      cell: (r) => (
        <div className="whitespace-nowrap text-sm text-slate-600">
          <div>{formatToIST(r.created_at, false)}</div>
          <div className="text-xs text-slate-500">{formatToIST(r.created_at).split(", ")[1]}</div>
        </div>
      ),
    },
    { key: "seller", header: "Seller", cell: (r) => (
      <div>
        <span className="text-sm font-medium text-slate-800">{r.seller_brand_name || r.seller_id || "-"}</span>
        <SellerBalanceById sellerId={r.seller_id} />
      </div>
    ) },
    {
      key: "type", header: "Type",
      cell: (r) =>
        r.type === "exchange" ? (
          <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200"><RefreshCw className="w-3 h-3 mr-1" />Exchange</Badge>
        ) : (
          <Badge variant="outline" className="border-slate-200 text-slate-700"><RotateCcw className="w-3 h-3 mr-1" />Return</Badge>
        ),
    },
    {
      key: "customer", header: "Customer",
      cell: (r) => (
        <div className="text-sm">
          <div className="font-medium text-slate-800">{r.buyer?.name || "-"}</div>
          <div className="text-xs text-slate-500">{[r.buyer?.city, r.buyer?.pincode].filter(Boolean).join(", ")}</div>
        </div>
      ),
    },
    { key: "reason", header: "Reason", hideOnMobile: true, cell: (r) => <span className="text-sm text-slate-700">{REASON_LABELS[r.reason] || r.reason || "-"}</span> },
    { key: "courier", header: "Courier", hideOnMobile: true, cell: (r) => <span className="text-sm text-slate-700">{r.courier_name || r.courier_code || <span className="text-slate-400">-</span>}</span> },
    { key: "awb", header: "AWB", hideOnMobile: true, cell: (r) => r.awb ? <span className="text-sm font-mono text-slate-700">{r.awb}</span> : <span className="text-sm text-slate-400">-</span> },
    {
      key: "aggregator", header: "Aggregator", hideOnMobile: true,
      cell: (r) => r.aggregator ? <span className="text-sm capitalize text-slate-700">{r.aggregator}</span> : <span className="text-sm text-slate-400">-</span>,
    },
    { key: "cost", header: "Cost", align: "right", cell: (r) => <span className="text-sm tabular-nums text-slate-700">{inr(r.cost_total)}</span> },
    { key: "charge", header: "Charge", align: "right", cell: (r) => <span className="text-sm tabular-nums text-slate-700">{inr(r.seller_charge)}</span> },
    {
      key: "margin", header: "Margin", align: "right",
      cell: (r) => r.margin == null
        ? <span className="text-sm tabular-nums text-slate-400">-</span>
        : <span className={`text-sm font-medium tabular-nums ${r.margin >= 0 ? "text-green-700" : "text-red-600"}`}>{inr(r.margin)}</span>,
    },
    { key: "status", header: "Status", cell: (r) => <StatusBadge status={r.status} kind="b2c" /> },
  ];

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  const emptyNode = (
    <EmptyState
      icon={RotateCcw}
      title="No returns match these filters"
      description={hasFilters ? "Try widening the seller, type, aggregator or date filters." : "No returns or exchanges have been created across any seller yet."}
      action={hasFilters ? <Button type="button" variant="outline" onClick={resetFilters}><RotateCcw className="w-4 h-4 mr-2" /> Clear filters</Button> : undefined}
    />
  );

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader title="Returns & Exchange" subtitle="All reverse shipments across sellers — cost, margin & status" />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Returns" value={Number(summary.return_count ?? 0).toLocaleString("en-IN")} icon={RotateCcw} accent="brand" loading={loading} />
          <StatCard label="Revenue" value={inr(summary.revenue_total ?? 0)} icon={Receipt} accent="info" loading={loading} trend="Reverse fees charged" />
          <StatCard label="Aggregator Cost" value={inr(summary.cost_total ?? 0)} icon={Wallet} accent="warning" loading={loading} trend="Paid to aggregators" />
          <StatCard label="Margin" value={inr(marginTotal ?? 0)} icon={TrendingUp} accent={marginAccent} loading={loading} trend={marginPctLine} />
        </div>

        <div className="flex gap-2 border-b border-slate-200 bg-white px-2 sm:px-6 rounded-t-lg shadow-sm overflow-x-auto">
          {TABS.map((tab) => {
            const c = tabCount(tab.id);
            return (
              <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                className={`px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id ? "border-brand text-brand" : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                }`}>
                {tab.label}{c != null && <span className="font-bold text-brand"> ({c})</span>}
              </button>
            );
          })}
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Seller</Label>
                <Select value={sellerId} onValueChange={setSellerId}>
                  <SelectTrigger><SelectValue placeholder="All sellers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All sellers</SelectItem>
                    {options.sellers.map((s) => <SelectItem key={s.seller_id} value={s.seller_id}>{s.brand_name || s.seller_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger><SelectValue placeholder="All types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All types</SelectItem>
                    <SelectItem value="return">Return</SelectItem>
                    <SelectItem value="exchange">Exchange</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Aggregator</Label>
                <Select value={aggregator} onValueChange={setAggregator}>
                  <SelectTrigger><SelectValue placeholder="All aggregators" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All aggregators</SelectItem>
                    {options.aggregators.map((a) => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600" htmlFor="admin-ret-from">From</Label>
                <Input id="admin-ret-from" type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600" htmlFor="admin-ret-to">To</Label>
                <Input id="admin-ret-to" type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600" htmlFor="admin-ret-search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input id="admin-ret-search" placeholder="Return #, order #, AWB or phone..." value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)} className="pl-9" />
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">Showing the last {DEFAULT_WINDOW_DAYS} days by default — widen “From” to see older returns.</p>
              {hasFilters && (
                <Button type="button" variant="ghost" size="sm" onClick={resetFilters}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Clear filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <DataTable
          columns={columns}
          data={data.items}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/admin/b2c/returns/${r.id}`)}
          loading={loading}
          empty={emptyNode}
          pagination={totalPages > 1 ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage } : undefined}
        />
      </div>
    </AdminLayout>
  );
};

export default B2CReturns;
